import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost } from '../utils/api';
import AudioPlayer from '../components/AudioPlayer';
import CopyUrlButton from '../components/CopyUrlButton';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import styles from './UserProfilePage.module.css';

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [friendRequestLoading, setFriendRequestLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState('none');
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);

  const {
    currentlyPlaying,
    audioElement,
    playbackTimes,
    durations,
    currentTimes,
    handlePlayPause,
    handleRewind
  } = useAudioPlayer();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Fetching profile for userId:', userId);
        const response = await apiGet(`/user/${userId}/friend-profile`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Profile data received:', data);
          setProfileData(data);
          
          // Check friendship status from the users endpoint
          await checkFriendshipStatus();
        } else {
          console.error('API Error:', response.status, response.statusText);
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          console.error('Error details:', errorData);
          setError(errorData.detail || `Failed to load user profile (${response.status})`);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  const checkFriendshipStatus = async () => {
    try {
      const response = await apiGet('/friendships/users');
      if (response.ok) {
        const usersData = await response.json();
        const targetUser = usersData.find(u => u.id === userId);
        if (targetUser && targetUser.friendship_status) {
          setFriendshipStatus(targetUser.friendship_status);
          // If we've sent a request, set requestSent to true
          if (targetUser.friendship_status === 'pending_sent') {
            setRequestSent(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking friendship status:', error);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!profileData?.user?.id) return;

    setFriendRequestLoading(true);
    try {
      const response = await apiPost('/friendships/send-request', {
        receiver_id: profileData.user.id
      });

      if (response.ok) {
        // Friend request sent successfully
        console.log('Friend request sent successfully');
        setRequestSent(true);
      } else {
        const errorData = await response.json();
        console.error('Failed to send friend request:', errorData);
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
    } finally {
      setFriendRequestLoading(false);
    }
  };

  const handleViewPodcastDetails = (podcast) => {
    setSelectedPodcast(podcast);
    // Use episodes from the API response instead of fetching separately
    setEpisodes(podcast.episodes || []);
  };


  const closeModal = () => {
    setSelectedPodcast(null);
    setEpisodes([]);
    if (audioElement) {
      audioElement.pause();
    }
  };

  if (loading) {
    return (
      <div className={styles.userProfilePage}>
        <div className="loading">Loading user profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.userProfilePage}>
        <div className="error-state">
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate(-1)} 
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={styles.userProfilePage}>
        <div className="error-state">
          <h2>User not found</h2>
          <button 
            onClick={() => navigate(-1)} 
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { user: profileUser, is_friend, public_podcasts } = profileData;

  return (
    <div className={styles.userProfilePage}>
      <div className={styles.pageHeader}>
        <button 
          onClick={() => navigate(-1)} 
          className="btn btn-secondary"
        >
          ← Back
        </button>
      </div>

      <div className={styles.profileHeader}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {profileUser.first_name?.[0]}{profileUser.last_name?.[0]}
          </div>
          <div className={styles.userDetails}>
            <h1>{profileUser.first_name} {profileUser.last_name}</h1>
            <div className={styles.friendStatus}>
              {is_friend ? (
                <span className={styles.friendBadge}>
                  ✓ Friends
                </span>
              ) : friendshipStatus === 'pending_sent' || requestSent ? (
                <span className={styles.requestSentBadge}>
                  Request Sent
                </span>
              ) : friendshipStatus === 'pending_received' ? (
                <span className={styles.pendingReceivedBadge}>
                  Request Received
                </span>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleSendFriendRequest}
                  disabled={friendRequestLoading}
                >
                  {friendRequestLoading ? 'Sending...' : 'Add Friend'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.podcastsSection}>
        <h2>Public Podcasts</h2>
        {!is_friend ? (
          <div className={styles.friendsOnlyMessage}>
            <p>You need to be friends with {profileUser.first_name} to see their podcasts.</p>
            <p>Send them a friend request to discover their content!</p>
          </div>
        ) : public_podcasts.length === 0 ? (
          <div className={styles.emptyPodcasts}>
            <p>{profileUser.first_name} hasn't created any public podcasts yet.</p>
          </div>
        ) : (
          <div className={styles.podcastsGrid}>
            {public_podcasts.map(podcast => (
              <div key={podcast.id} className={styles.podcastCard}>
                <div className={styles.podcastInfo}>
                  <h3>{podcast.title}</h3>
                  {podcast.prompt && (
                    <p className={styles.podcastPrompt}>
                      {podcast.prompt}
                    </p>
                  )}
                </div>
                <div className={styles.podcastFooter}>
                  <div className={styles.podcastMeta}>
                    <span className={styles.frequency}>
                      {podcast.frequency === 'none' ? 'Ad-hoc' : podcast.frequency}
                    </span>
                    <span className={styles.duration}>
                      {podcast.duration} min
                    </span>
                    <span className={`${styles.status} ${styles[podcast.status]}`}>
                      {podcast.status === 'active' && <span className={styles.checkmark}>✓</span>}
                      {podcast.status === 'active' ? 'Active' : podcast.status}
                    </span>
                    <span className={styles.createdDate}>
                      {new Date(podcast.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className={styles.podcastActions}>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleViewPodcastDetails(podcast)}
                    >
                      Episodes
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Podcast Details Modal */}
      {selectedPodcast && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{selectedPodcast.title}</h2>
              <button 
                className={styles.closeButton}
                onClick={closeModal}
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              {episodesLoading ? (
                <div className={styles.loading}>Loading episodes...</div>
              ) : episodes.length === 0 ? (
                <div className={styles.emptyState}>No episodes found</div>
              ) : (
                <div className={styles.episodesList}>
                  {episodes.map(episode => (
                    <div key={episode.id} className={styles.episodeCard}>
                      <div className={styles.episodeInfo}>
                        <div className={styles.episodeTitleRow}>
                          <CopyUrlButton episode={episode} />
                          <h3>{episode.title}</h3>
                        </div>
                        {episode.notes && <p className={styles.episodeDescription}>{episode.notes}</p>}
                        <div className={styles.episodeMeta}>
                          <span className={styles.episodeNumber}>Episode #{episode.episode_number}</span>
                          {episode.audio_files && episode.audio_files.length > 0 && episode.audio_files[0].duration_seconds && (
                            <span className={styles.duration}>
                              {Math.floor(episode.audio_files[0].duration_seconds / 60)}:{(episode.audio_files[0].duration_seconds % 60).toString().padStart(2, '0')}
                            </span>
                          )}
                          <span className={styles.publishDate}>
                            {new Date(episode.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className={styles.episodeActions}>
                        <AudioPlayer
                          episode={episode}
                          isPlaying={currentlyPlaying === episode.id}
                          onPlayPause={handlePlayPause}
                          onRewind={handleRewind}
                          currentTime={currentTimes[episode.id] || 0}
                          duration={durations[episode.id] || 0}
                          playbackTime={playbackTimes[episode.id] || 0}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
